import {Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {
  DateRange,
  DefaultMatCalendarRangeStrategy,
  MAT_DATE_RANGE_SELECTION_STRATEGY,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import {MatNativeDateModule} from '@angular/material/core';
import {CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition} from '@angular/cdk/overlay';
import {TimePickerComponent} from '../time-picker/time-picker.component';
import {ClickOutsideServiceDirective} from '../../directives/click-outside-service.directive';
import {EscapeKeyDirective} from '../../directives/escape-key.directive';
import {DayPeriod, Time12Hour, TimeConverter} from '../../../shared/models/time';

@Component({
  selector: 'motn-datetime-range-picker',
  templateUrl: './datetime-range-picker.component.html',
  styleUrls: ['./datetime-range-picker.component.scss'],
  imports: [
    MatDatepickerModule,
    MatNativeDateModule,
    CdkOverlayOrigin,
    CdkConnectedOverlay,
    TimePickerComponent,
    ClickOutsideServiceDirective,
    EscapeKeyDirective,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {provide: MAT_DATE_RANGE_SELECTION_STRATEGY, useClass: DefaultMatCalendarRangeStrategy},
  ],
})
export class DatetimeRangePickerComponent implements OnInit {
  @Input() startValue: Date | null = null;
  @Input() endValue: Date | null = null;
  @Input() maxDate: Date = new Date();
  @Input() getMaxEndDate: ((startDate: Date) => Date) | null = null;

  @Output() rangeChange = new EventEmitter<{start: Date; end: Date}>();
  @Output() closed$ = new EventEmitter<void>();

  selectedRange: DateRange<Date> | null = null;

  readonly positions: ConnectedPosition[] = [
    // Below trigger first — picker is tall and the trigger is typically near the top of the screen.
    {originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 8},
    {originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 8},
    // Above trigger fallback.
    {originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -8},
    {originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -8},
  ];

  startTime: Time12Hour = {hour: 12, minute: 0, second: 0, period: DayPeriod.AM};
  endTime: Time12Hour = {hour: 11, minute: 59, second: 59, period: DayPeriod.PM};

  get canApply(): boolean {
    return !!(this.selectedRange?.start && this.selectedRange?.end);
  }

  readonly dateFilter = (date: Date | null): boolean => {
    if (!date) return true;
    if (date > this.maxDate) return false;
    // Restrict end-date candidates to the protocol's seek-length window from the start.
    if (this.selectedRange?.start && !this.selectedRange?.end && this.getMaxEndDate) {
      return date <= this.getMaxEndDate(this.selectedRange.start);
    }
    return true;
  };

  ngOnInit(): void {
    const s = this.startValue;
    const e = this.endValue;

    if (s) this.startTime = TimeConverter.from24Hour(s.getHours(), s.getMinutes(), s.getSeconds());
    if (e) this.endTime = TimeConverter.from24Hour(e.getHours(), e.getMinutes(), e.getSeconds());

    if (s && e) {
      // Strip time component — date-only DateRange so mat-calendar highlights the previous selection on open.
      this.selectedRange = new DateRange(
        new Date(s.getFullYear(), s.getMonth(), s.getDate()),
        new Date(e.getFullYear(), e.getMonth(), e.getDate()),
      );
    }
  }

  onDateSelected(date: Date | null): void {
    if (!date) return;
    // Mirrors DefaultMatCalendarRangeStrategy.selectionFinished: start fresh, or close range if date ≥ start.
    const start = this.selectedRange?.start ?? null;
    const end = this.selectedRange?.end ?? null;
    if (start && !end && date >= start) {
      this.selectedRange = new DateRange(start, date);
    } else {
      this.selectedRange = new DateRange(date, null);
    }
  }

  updateStartTime(changes: Partial<Time12Hour>): void {
    this.startTime = {...this.startTime, ...changes};
  }

  updateEndTime(changes: Partial<Time12Hour>): void {
    this.endTime = {...this.endTime, ...changes};
  }

  onApply(): void {
    const start = this.selectedRange?.start;
    const end = this.selectedRange?.end;
    if (!start || !end) return;

    const s = TimeConverter.to24Hour(this.startTime);
    const e = TimeConverter.to24Hour(this.endTime);

    this.rangeChange.emit({
      start: new Date(start.getFullYear(), start.getMonth(), start.getDate(), s.hour, s.minute, s.second),
      end: new Date(end.getFullYear(), end.getMonth(), end.getDate(), e.hour, e.minute, e.second, 999),
    });
  }
}
